import { unstable_cache } from "next/cache";
import { SitecoreClient } from "@sitecore-content-sdk/nextjs/client";
import type {
  ComponentMap,
  ComponentPropsCollection,
  NextjsContentSdkComponent,
  Page,
} from "@sitecore-content-sdk/nextjs";
import type {
  FetchOptions,
  PageOptions,
} from "@sitecore-content-sdk/content/client";
import scConfig from "sitecore.config";
import { applyLayoutRoutes } from "./layout-routes/apply-layout-routes";
import {
  normalizePath,
  normalizeRequestLocale,
  normalizeSite,
  routeTag,
  SHARED_LAYOUT_STALENESS_SECONDS,
} from "./cache/tags";

export type CachedPageData = {
  page: Page;
  componentProps: ComponentPropsCollection;
};

class EcolabSitecoreClient extends SitecoreClient {
  override async getPage(
    path: string | string[],
    pageOptions?: PageOptions,
    fetchOptions?: FetchOptions,
  ): Promise<Page | null> {
    const page = await super.getPage(path, pageOptions ?? {}, fetchOptions);
    if (!page) return null;

    const language = pageOptions?.locale ?? scConfig.defaultLanguage ?? "en-US";

    return applyLayoutRoutes(page, this, language);
  }

  async getCachedPageWithComponents(
    path: string | string[],
    pageOptions: PageOptions,
    componentMap: ComponentMap<NextjsContentSdkComponent>,
  ): Promise<CachedPageData | null> {
    const site = normalizeSite(pageOptions.site);
    const locale = normalizeRequestLocale(pageOptions.locale ?? "");
    const normalizedPath = normalizePath(
      Array.isArray(path) ? path.join("/") : path,
    );

    const fetchPageWithComponents = unstable_cache(
      async (): Promise<CachedPageData | null> => {
        const page = await this.getPage(path, pageOptions);
        if (!page) return null;

        const componentProps = await this.getComponentData(
          page.layout,
          {},
          componentMap,
        );

        return { page, componentProps };
      },
      ["sc-route-page", site, locale, normalizedPath],
      {
        tags: [routeTag(site, locale, normalizedPath)],
        revalidate: SHARED_LAYOUT_STALENESS_SECONDS,
      },
    );

    return fetchPageWithComponents();
  }
}

const client = new EcolabSitecoreClient({
  ...scConfig,
});

export default client;
